import type { Assets } from '../core/assets.js';
import type { Screen } from '../core/screen.js';
import type { AudioSystem } from '../audio/audio.js';
import { SEG_TITLE } from '../audio/music.js';
import { profileStore, type ProfileStore, type MatchResult } from './profile.js';
import { drawRecords } from '../render/records.js';
export interface MenuResult {
  playerPilot: number;
  opponentPilot: number;
  seed: number;
  practice?: boolean;
}
export type MenuState =
  | 'TITLE'
  | 'PILOT_SELECT'
  | 'OPPONENT_SELECT'
  | 'MODE'
  | 'OPTIONS'
  | 'RECORDS'
  | 'HELP'
  | 'QUIT';
export function menuLayout(state: MenuState): {
  top: number;
  height: number;
} {
  if (state === 'PILOT_SELECT' || state === 'OPPONENT_SELECT') return { top: 34, height: 18 };
  if (state === 'RECORDS') return { top: 112, height: 12 };
  if (state === 'HELP') return { top: 106, height: 12 };
  return { top: 34, height: 14 };
}
export function menuRowAt(state: MenuState, y: number, count: number): number | null {
  const row = Math.floor((y - menuLayout(state).top) / menuLayout(state).height);
  return row >= 0 && row < count ? row : null;
}
export class MenuScene {
  private state: MenuState = 'TITLE';
  private cursor = 0;
  private playerPilot = 0;
  private opponentPilot = 0;
  private page = 0;
  private message = '';
  private messageTicks = 0;
  private musicArmed = true;
  private readonly keyDown = (e: KeyboardEvent): void => {
    if (['ArrowUp', 'w', 'W', '2'].includes(e.key)) {
      e.preventDefault();
      this.moveCursor(-1);
    } else if (['ArrowDown', 's', 'S', '8'].includes(e.key)) {
      e.preventDefault();
      this.moveCursor(1);
    } else if (['Enter', ' ', 'z', 'Z', '5'].includes(e.key)) {
      e.preventDefault();
      this.activate();
    } else if (['Escape', 'Backspace'].includes(e.key)) {
      e.preventDefault();
      this.back();
    }
  };
  private readonly pointer = (e: PointerEvent): void => {
    const canvas = document.getElementById('screen');
    if (!(canvas instanceof HTMLCanvasElement) || e.target !== canvas) return;
    const r = canvas.getBoundingClientRect();
    const y = ((e.clientY - r.top) / r.height) * 128;
    const row = menuRowAt(this.state, y, this.entries().length);
    if (row === null) return;
    e.preventDefault();
    this.selectRow(row);
  };
  constructor(
    private readonly screen: Screen,
    private readonly assets: Assets,
    private readonly audio: AudioSystem | null,
    private readonly onFight: (result: MenuResult) => void,
    private readonly store: ProfileStore = profileStore,
    private readonly onNetwork?: (pilot: number) => void,
  ) {
    if (
      typeof location !== 'undefined' &&
      new URLSearchParams(location.search).get('menu') === 'pilots'
    )
      this.state = 'PILOT_SELECT';
    if (audio) audio.bus.enabled = store.profile.sound;
  }
  get currentState(): MenuState {
    return this.state;
  }
  get currentEntries(): readonly string[] {
    return this.entries();
  }
  selectRow(row: number): void {
    if (!Number.isInteger(row) || row < 0 || row >= this.entries().length) return;
    this.cursor = row;
    this.activate();
  }
  attach(): void {
    window.addEventListener('keydown', this.keyDown);
    window.addEventListener('pointerdown', this.pointer);
  }
  detach(): void {
    window.removeEventListener('keydown', this.keyDown);
    window.removeEventListener('pointerdown', this.pointer);
  }
  reportResult(opponent: number, won: boolean, result: MatchResult): void {
    if (won) this.store.recordWin(opponent, result);
  }
  private tm(i: number, fallback = ''): string {
    return this.assets.strings.tm[i] ?? fallback;
  }
  private enter(state: MenuState): void {
    this.state = state;
    this.cursor = 0;
    this.messageTicks = 0;
  }
  private entries(): string[] {
    switch (this.state) {
      case 'TITLE':
        return [this.tm(1), this.tm(7), this.tm(10), this.tm(11), this.tm(12), this.tm(21)];
      case 'PILOT_SELECT':
        return this.assets.strings.tm.slice(3, 7);
      case 'OPPONENT_SELECT':
        return this.assets.strings.tm.slice(26, 30);
      case 'MODE':
        return [this.tm(34), this.tm(35), this.tm(19)];
      case 'OPTIONS':
        return [
          `${this.tm(13)} ${this.store.profile.taunts ? this.tm(14) : this.tm(15)}`,
          `${this.tm(16)} ${this.store.profile.sound ? this.tm(17) : this.tm(18)}`,
          this.tm(19),
        ];
      case 'HELP':
        return [this.page < 2 ? (this.assets.strings.tg[6] ?? 'Next') : this.tm(19)];
      case 'RECORDS':
        return [this.tm(19)];
      case 'QUIT':
        return [this.tm(23), this.tm(24)];
    }
  }
  private flash(text: string): void {
    this.message = text;
    this.messageTicks = 60;
  }
  private moveCursor(delta: number): void {
    const n = this.entries().length;
    this.cursor = (this.cursor + delta + n) % n;
  }
  private back(): void {
    if (this.state === 'OPPONENT_SELECT') this.enter('PILOT_SELECT');
    else if (this.state === 'MODE') this.enter('OPPONENT_SELECT');
    else this.enter('TITLE');
  }
  private activate(): void {
    switch (this.state) {
      case 'TITLE': {
        if (this.cursor === 0) this.enter('PILOT_SELECT');
        else if (this.cursor === 1) {
          if (this.onNetwork) this.onNetwork(this.playerPilot);
          else this.flash('Arena connection unavailable');
        } else if (this.cursor === 2) {
          this.page = 0;
          this.enter('HELP');
        } else if (this.cursor === 3) this.enter('RECORDS');
        else if (this.cursor === 4) this.enter('OPTIONS');
        else this.enter('QUIT');
        break;
      }
      case 'PILOT_SELECT':
      case 'OPPONENT_SELECT': {
        if (!this.store.isUnlocked(this.cursor)) {
          this.flash(this.assets.strings.tg[8] ?? 'Locked');
          break;
        }
        if (this.state === 'PILOT_SELECT') {
          this.playerPilot = this.cursor;
          this.enter('OPPONENT_SELECT');
        } else {
          this.opponentPilot = this.cursor;
          this.enter('MODE');
        }
        break;
      }
      case 'MODE':
        if (this.cursor === 2) this.back();
        else
          this.onFight({
            playerPilot: this.playerPilot,
            opponentPilot: this.opponentPilot,
            seed: Date.now() & 0x7fffffff || 1,
            practice: this.cursor === 1,
          });
        break;
      case 'OPTIONS':
        if (this.cursor === 0) this.store.setTaunts(!this.store.profile.taunts);
        else if (this.cursor === 1) {
          this.store.setSound(!this.store.profile.sound);
          if (this.audio) {
            this.audio.bus.enabled = this.store.profile.sound;
            if (!this.audio.bus.enabled) this.audio.music.stop();
          }
        } else this.back();
        break;
      case 'HELP':
        if (this.page++ >= 2) this.back();
        break;
      case 'RECORDS':
        this.back();
        break;
      case 'QUIT':
        if (this.cursor === 0) {
          this.audio?.music.stop();
          this.flash('Game closed - select No to return');
        } else this.back();
        break;
    }
  }
  update(): void {
    if (this.messageTicks > 0) this.messageTicks--;
    if (this.musicArmed && this.audio?.music.play(SEG_TITLE)) this.musicArmed = false;
  }
  render(_fps: number): void {
    const ctx = this.screen.ctx,
      font = this.assets.fontMain,
      tm = this.assets.strings.tm;
    this.screen.clear('#b9dce0');
    const titles: Record<MenuState, string> = {
      TITLE: 'BIPLANES.IO',
      PILOT_SELECT: tm[2] ?? '',
      OPPONENT_SELECT: tm[25] ?? '',
      MODE: tm[33] ?? '',
      OPTIONS: tm[12] ?? '',
      RECORDS: tm[11] ?? '',
      HELP: tm[10] ?? '',
      QUIT: tm[22] ?? '',
    };
    font
      .wrap(titles[this.state], 120)
      .forEach((line, i) => font.drawCentered(ctx, line, 64, 4 + i * 10));
    const layout = menuLayout(this.state);
    this.entries().forEach((entry, i) => {
      const y = layout.top + i * layout.height;
      const locked =
        (this.state === 'PILOT_SELECT' || this.state === 'OPPONENT_SELECT') &&
        !this.store.isUnlocked(i);
      if (i === this.cursor) {
        ctx.fillStyle = '#f1ca67';
        ctx.fillRect(2, y - 2, 124, layout.height);
      }
      const base = this.state === 'PILOT_SELECT' ? 43 : this.state === 'OPPONENT_SELECT' ? 47 : -1;
      if (base >= 0) {
        const icon = this.assets.sprites.get(
          `fx_${String(locked ? (base === 43 ? 51 : 52) : base + i).padStart(2, '0')}`,
        );
        if (icon) ctx.drawImage(icon, 5, y - 1);
      }
      font.draw(ctx, locked ? (this.assets.strings.tg[5] ?? '????') : entry, base >= 0 ? 25 : 5, y);
    });
    if (this.state === 'RECORDS') {
      drawRecords(ctx, this.assets, this.store.profile);
    }
    if (this.state === 'HELP') {
      const texts = [
        '2 W or Up: Accelerate\n8 S or Down: Decelerate\n4 A or Left: Turn\n6 D or Right: Turn\n5 Z or Space: Fire',
        'E or #: Eject\nPress E again: Parachute\n4 and 6: Steer parachute\nLand gently then run into the barn to fly again.',
        'Touch: top fires\nTop right: eject\nMiddle left and right: turn\nBottom left: accelerate\nBottom right: decelerate\nP or Escape: pause',
      ];
      font
        .wrap(texts[this.page] ?? '', 118)
        .slice(0, 8)
        .forEach((line, i) => font.draw(ctx, line, 5, 25 + i * 10));
    }
    if (this.messageTicks > 0) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(2, 34, 124, 60);
      font
        .wrap(this.message, 118)
        .slice(0, 5)
        .forEach((line, i) => font.draw(ctx, line, 5, 38 + i * 10));
    }
  }
}
